import React, { createContext, useEffect, useReducer, useContext } from "react";
import axios from "axios";

const INITIAL_STATE = {
  user: null,
  loading: false,
  error: null,
};

const AuthContext = createContext(INITIAL_STATE);

const AuthReducer = (state, action) => {
  switch (action.type) {
    case "LOGIN_START":
    case "REGISTER_START":
    case "UPDATE_START":
      return {
        ...state,
        loading: true,
        error: null,
      };
    case "LOGIN_SUCCESS":
    case "REGISTER_SUCCESS":
      return {
        user: action.payload,
        loading: false,
        error: null,
      };
    case "UPDATE_SUCCESS":
      return {
        ...state,
        user: action.payload,
        loading: false,
        error: null,
      };
    case "LOGIN_FAILURE":
    case "REGISTER_FAILURE":
    case "UPDATE_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "LOGOUT":
      return {
        user: null,
        loading: false,
        error: null,
      };
    case "CLEAR_ERROR":
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

export const AuthContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(AuthReducer, INITIAL_STATE);

  useEffect(() => {
    const checkUserSession = () => {
      const storedUser = localStorage.getItem("user");
      const lastActivity = localStorage.getItem("lastActivity");

      if (storedUser && lastActivity) {
        const now = new Date().getTime();
        const timeSinceLastActivity = now - parseInt(lastActivity, 10);

      }
    };

    checkUserSession();

    const updateLastActivity = () => {
      if (state.user) {
        localStorage.setItem("lastActivity", new Date().getTime().toString());
      }
    };

    // Update last activity on user interactions
    window.addEventListener("mousemove", updateLastActivity);
    window.addEventListener("keydown", updateLastActivity);

    return () => {
      window.removeEventListener("mousemove", updateLastActivity);
      window.removeEventListener("keydown", updateLastActivity);
    };
  }, [state.user]);

  useEffect(() => {
    if (state.user) {
      localStorage.setItem("user", JSON.stringify(state.user));
      localStorage.setItem("lastActivity", new Date().getTime().toString());
    }
  }, [state.user]);

  return (
    <AuthContext.Provider
      value={{
        user: state.user,
        loading: state.loading,
        error: state.error,
        dispatch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const login = async (dispatch, credentials) => {
  dispatch({ type: "LOGIN_START" });
  try {
    const response = await axios.post(
      "https://api-r2ug.onrender.com/api/auth/login",
      credentials
    );
    dispatch({ type: "LOGIN_SUCCESS", payload: response.data.details });
    return response.data.details;
  } catch (error) {
    dispatch({ type: "LOGIN_FAILURE", payload: error.response.data });
    throw error;
  }
};

export const register = async (dispatch, userData) => {
  dispatch({ type: "REGISTER_START" });
  try {
    const response = await axios.post(
      "https://api-r2ug.onrender.com/api/auth/register",
      userData
    );
    dispatch({ type: "REGISTER_SUCCESS", payload: response.data.details });
    return response.data.details;
  } catch (error) {
    dispatch({ type: "REGISTER_FAILURE", payload: error.response.data });
    throw error;
  }
};

export const updateUser = async (dispatch, userId, userData) => {
  dispatch({ type: "UPDATE_START" });
  try {
    const response = await axios.put(
      `https://api-r2ug.onrender.com/api/users/${userId}`,
      userData
    );
    dispatch({ type: "UPDATE_SUCCESS", payload: response.data });
    return response.data;
  } catch (error) {
    dispatch({ type: "UPDATE_FAILURE", payload: error.response.data });
    throw error;
  }
};

export const logout = (dispatch) => {
  localStorage.removeItem("user");
  localStorage.removeItem("lastActivity");
  dispatch({ type: "LOGOUT" });
};

export { AuthContext };
